import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download, TrendingUp } from "lucide-react";
import api, { API } from "@/lib/api";
import { PageTitle } from "./DashboardLayout";
import { Button } from "@/components/ui/button";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const TITLES = {
  leads_by_source: "Leads by Source", leads_by_service: "Leads by Service", revenue_by_month: "Revenue by Month",
  invoices_outstanding: "Outstanding Invoices", digital_sales: "Digital Product Sales", appointments_by_status: "Appointments by Status",
};

export default function Reports() {
  const [range, setRange] = useState({ frm: "", to: "" });
  const { data, refetch } = useQuery({ queryKey: ["reports", range], queryFn: () => api.get("/reports/summary", { params: { frm: range.frm || undefined, to: range.to || undefined } }).then(r => r.data) });
  const k = data?.kpis || {};
  const input = "h-9 px-2 rounded-lg border border-input text-sm";
  const csvUrl = (name) => `${API}/reports/${name}/csv${range.frm || range.to ? `?frm=${range.frm}&to=${range.to}` : ""}`;

  return (
    <div>
      <PageTitle title="Reports" subtitle="Business analytics with CSV export" action={
        <div className="flex gap-2 items-center">
          <input type="date" className={input} value={range.frm} onChange={e => setRange({ ...range, frm: e.target.value })} data-testid="report-from" />
          <span className="text-slate-400 text-sm">→</span>
          <input type="date" className={input} value={range.to} onChange={e => setRange({ ...range, to: e.target.value })} data-testid="report-to" />
          <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="report-apply">Apply</Button>
        </div>
      } />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[["Total Leads", k.totalLeads], ["Conversion", (k.conversionRate ?? 0) + "%"], ["Revenue Collected", money(k.revenueCollected)], ["Outstanding", money(k.outstanding)], ["Digital Sales", k.digitalSales], ["AI Conversations", k.aiConversations], ["Appointments", k.appointments]].map(([label, val]) => (
          <div key={label} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
            <p className="text-xs font-mono uppercase tracking-wider text-metallic">{label}</p>
            <p className="mt-2 text-2xl font-heading font-bold text-navy">{val ?? "—"}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {Object.keys(TITLES).map((name) => {
          const rep = data?.[name];
          return (
            <div key={name} className="rounded-xl bg-white border border-slate-200 p-5" data-testid={`report-${name}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-semibold text-navy flex items-center gap-2"><TrendingUp className="h-4 w-4 text-electric" /> {TITLES[name]}</h3>
                <a href={csvUrl(name)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="gap-1.5" data-testid={`csv-${name}`}><Download className="h-3.5 w-3.5" /> CSV</Button></a>
              </div>
              <div className="overflow-x-auto max-h-64">
                <table className="w-full text-sm">
                  <thead className="text-slate-500 text-xs uppercase font-mono"><tr>{(rep?.headers || []).map(h => <th key={h} className="text-left p-2">{h}</th>)}</tr></thead>
                  <tbody>
                    {(rep?.rows || []).length === 0 ? <tr><td className="p-3 text-slate-400 text-sm">No data in range.</td></tr> :
                      (rep?.rows || []).map((row, i) => (
                        <tr key={i} className="border-t border-slate-100"><>{row.map((c, j) => <td key={j} className="p-2 text-slate-700">{c}</td>)}</></tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
